import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import React from "react";

type ChannelStoreModel = Record<Moim.Id, Moim.Channel.SimpleChannelType>;

const CHANNEL_QUERY_KEY = ["channels"] as const;

export const useChannelQuery = () => {
  const { data, ...rest } = useSuspenseQuery<ChannelStoreModel>({
    queryKey: CHANNEL_QUERY_KEY,
    queryFn: async () => {
      // NOTE: 지금은 channel list를 가져올 필요가 없어서 빈 객체를 반환.
      return {};
    },
    initialData: {},
    initialDataUpdatedAt: 0,
    staleTime: 1000 * 60 * 60, // 1 hour
    select: data => {
      return data;
    },
  });

  return {
    channels: data,
    ...rest,
  };
};

export const useMutationChannels = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: Moim.Channel.SimpleChannelType) => data,
    onMutate: async (data: Moim.Channel.SimpleChannelType) => {
      const touchKey = CHANNEL_QUERY_KEY;

      await queryClient.cancelQueries({
        queryKey: touchKey,
      });

      const previousChannels =
        queryClient.getQueryData<ChannelStoreModel>(touchKey);

      let touchedChannel: Moim.Channel.SimpleChannelType = data;
      const prevChannel = previousChannels[data.id];
      if (prevChannel) {
        touchedChannel = {
          ...prevChannel,
          ...data,
        };
      }

      queryClient.setQueryData<ChannelStoreModel>(touchKey, {
        ...previousChannels,
        [data.id]: touchedChannel,
      });

      return { previousChannels };
    },
    onError(err, newData, context) {
      if (context?.previousChannels) {
        queryClient.setQueryData(CHANNEL_QUERY_KEY, context.previousChannels);
      }
    },
  });

  const storeChannel = React.useCallback(
    (newChannel: Moim.Channel.SimpleChannelType) => {
      try {
        mutation.mutate(newChannel);
      } catch {
        console.error("Failed to store thread");
      }
    },
    [mutation],
  );

  return {
    ...mutation,
    storeChannel,
  };
};
